// Copyright (C) 2019-2022 Intel Corporation
//
// SPDX-License-Identifier: MIT

import React, { useEffect, useState } from 'react';
import { AutoComplete } from 'antd';
import { getCore } from 'cvat-core-wrapper';
import AnnotationLaw from 'cvat-core/src/annotation-law';
import CVATTooltip from 'components/common/cvat-tooltip';
import Text from 'antd/lib/typography/Text';

const core = getCore();

interface Props {
    disabled: boolean;
    annotationLaws?: Record<string, AnnotationLaw>;
    value: AnnotationLaw | null;
    onChange: (value: AnnotationLaw | null) => void;
}

const renderItem = (law: AnnotationLaw) => ({
    value: law.name,
    label: (
        <>
            <CVATTooltip
                title={`${law.url}`}
                className='cvat-analytics-tooltip'
                overlayStyle={{ maxWidth: '500px' }}
            >
                <Text strong className='cvat-text-block'>
                    {law.name}
                </Text>
            </CVATTooltip>
        </>
    ),
});

export default function AnnotationLawField(props: Props): JSX.Element {
    const {
        disabled, annotationLaws, value, onChange,
    } = props;

    const [internalValue, setInternalValue] = useState<AnnotationLaw | null>();
    const [internalLaws, setInternalLaws] = useState<Record<string, AnnotationLaw>>({});

    useEffect(() => {
        if (!annotationLaws) {
            core.annotationLaws.get().then((_annotationLaws: AnnotationLaw[]) => {
                setInternalLaws(_annotationLaws.reduce((acc, law) => {
                    acc[law.name] = law;
                    return acc;
                }, {} as Record<string, AnnotationLaw>));
            });
        } else {
            setInternalLaws(annotationLaws || {});
        }
    }, [annotationLaws]);

    useEffect(() => {
        setInternalValue(value);
    }, [value]);

    return (
        <AutoComplete
            value={internalValue?.name}
            allowClear
            className='cvat-annotation-law-field'
            onChange={(selected: string) => {
                if (selected !== internalValue?.name) {
                    onChange(internalLaws[selected] || null);
                }
                setInternalValue(internalLaws[selected] || null);
            }}
            options={Object.keys(internalLaws).map(
                (lawName) => (renderItem(internalLaws[lawName])),
            )}
            disabled={disabled}
        />
    );
}
